import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getTokens } from '@shared/api';

import { createUsersMutationFn, updateUserMutationFn } from './mutations';
import { currentUserQueryFn, userQueryFn } from './queries';
import { CURRENT_USER_QUERY_KEYS, USER_QUERY_KEYS } from './queryKeys';

import type { UserQueryKeys } from './queryKeys';
import type {
  CreateUserPayload,
  CreateUserError,
  UpdateUserPayload,
  UserModel,
  UpdateUserError,
} from './types';
import type { CommonApiError } from '@shared/types';
import type {
  DefaultError,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';

const isCurrentUserQueryEnabled = () => Boolean(getTokens()?.refresh);

export const useUser = (
  options: UseQueryOptions<UserModel, CommonApiError, UserModel, UserQueryKeys>,
) =>
  useQuery({
    ...options,
    queryFn: userQueryFn,
  });

export const useCurrentUser = () =>
  useQuery<UserModel, DefaultError, UserModel, string[]>({
    queryFn: currentUserQueryFn,
    enabled: isCurrentUserQueryEnabled,
    queryKey: CURRENT_USER_QUERY_KEYS,
    retryOnMount: false,
    retry: false,
  });

export const useCreateUsers = (
  options?: UseMutationOptions<
    UserModel[],
    CreateUserError,
    { users: CreateUserPayload[] }
  >,
) =>
  useMutation({
    ...options,
    mutationFn: createUsersMutationFn,
  });

export const useUpdateUser = (
  options?: UseMutationOptions<UserModel, UpdateUserError, UpdateUserPayload>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateUserMutationFn,
    onSuccess: (...args) => {
      const [data] = args;
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS(data.id) });
      options?.onSuccess?.(...args);
    },
  });
};
